import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_META } from "@/lib/meta";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Upload } from "lucide-react";
import BulkUpload from "@/components/BulkUpload";

const USER_CSV_TEMPLATE =
  "name,email,password,role\n" +
  "Jane Rigger,jane@example.com,secret123,rigger\n";


const emptyForm = { name: "", email: "", password: "", role: "rigger", locations: [] };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);

  useEffect(() => {
    load();
    api.get("/locations").then((r) => setLocations(r.data));
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, locations: u.locations || [], dual_role: !!u.dual_role });
    setOpen(true);
  };

  const toggleLocation = (locName) => {
    setForm((f) => ({
      ...f,
      locations: f.locations.includes(locName)
        ? f.locations.filter((l) => l !== locName)
        : [...f.locations, locName],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const locs = form.role === "admin" ? [] : form.locations;
      const dual = ["rigger", "service_technician"].includes(form.role) && !!form.dual_role;
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role, locations: locs, dual_role: dual };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", { ...form, locations: locs, dual_role: dual });
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("User deleted");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">
            Administration
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">
            Users
          </h1>
        </div>
        <div className="flex gap-3">
          {user.role === "admin" && (
            <button
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-upload-users-button"
              className="bg-zinc-100 text-zinc-900 border border-zinc-300 rounded-none font-bold tracking-tight hover:bg-zinc-200 transition-colors px-5 py-2.5 flex items-center gap-2"
            >
              <Upload size={18} /> Upload CSV
            </button>
          )}
          <button
            onClick={openCreate}
            data-testid="new-user-button"
            className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2"
          >
            <Plus size={18} /> New User
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
              <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Name</th>
              <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Email</th>
              <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Role</th>
              <th className="px-4 py-3 font-bold text-zinc-600 uppercase text-xs tracking-wider">Locations</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-zinc-50 transition-colors">
                <td className="px-4 py-3 font-semibold text-zinc-900">{u.name}</td>
                <td className="px-4 py-3 text-zinc-600 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${ROLE_META[u.role].cls}`}>
                    {ROLE_META[u.role].label}{u.dual_role ? " + Service Technician" : ""}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span className="text-xs text-zinc-400">All locations</span>
                  ) : u.locations && u.locations.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {u.locations.map((l) => (
                        <span key={l} className="text-[10px] font-semibold px-2 py-0.5 bg-zinc-100 border border-zinc-300 text-zinc-700">
                          {l}
                        </span>
                      ))}
                    </div>
                  ) : ["rigger", "manager"].includes(u.role) ? (
                    <span className="text-xs text-red-500">None</span>
                  ) : (
                    <span className="text-xs text-zinc-400">All locations</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {u.id === user.id ? (
                      <span className="text-xs text-zinc-400 italic" data-testid={`user-self-${u.id}`}>You</span>
                    ) : (
                      <>
                        <button
                          onClick={() => openEdit(u)}
                          data-testid={`edit-user-${u.id}`}
                          className="text-zinc-400 hover:text-brand transition-colors"
                        >
                          <Pencil size={16} />
                        </button>
                        {user.role === "admin" && (
                          <button
                            onClick={() => setDeleteId(u.id)}
                            data-testid={`delete-user-${u.id}`}
                            className="text-zinc-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
              {editing ? "Edit User" : "New User"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Name</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="user-name-input"
                className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-email-input"
                className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand disabled:bg-zinc-100"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">
                  Password {editing && <span className="text-zinc-400 font-normal">(leave blank)</span>}
                </label>
                <input
                  type="password"
                  required={!editing}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="user-password-input"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Role</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="user-role-select"
                  className="w-full px-3 py-2 border border-zinc-300 rounded-none text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="rigger">Rigger</option>
                  {(user.role === "admin" || (editing && editing.role === "manager")) && <option value="manager">Manager</option>}
                  {user.role === "admin" && <option value="hire_manager">Services Manager</option>}
                  {user.role === "admin" && <option value="hire_coordinator">Services Coordinator</option>}
                  {user.role === "admin" && <option value="service_technician">Service Technician</option>}
                  {user.role === "admin" && <option value="job_dispatcher">Job Dispatcher</option>}
                  {user.role === "admin" && <option value="viewer">Viewer (read-only)</option>}
                  {user.role === "admin" && <option value="admin">Administrator</option>}
                </select>
              </div>
            </div>
            {["admin", "superadmin"].includes(user.role) && ["rigger", "service_technician"].includes(form.role) && (
              <label className="flex items-center gap-2 text-sm font-semibold text-zinc-800 cursor-pointer bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="user-dual-role">
                <input type="checkbox" checked={!!form.dual_role} onChange={(e) => setForm({ ...form, dual_role: e.target.checked })}
                  data-testid="user-dual-role-checkbox" className="accent-brand w-4 h-4" />
                {form.role === "rigger" ? "Also Service Technician" : "Also Rigger"} — sees both their rigging jobs and service jobs
              </label>
            )}
            {form.role !== "admin" && (
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-2">
                  Assigned Locations{" "}
                  <span className="text-zinc-400 font-normal">
                    {["rigger", "manager"].includes(form.role) ? "(user only sees jobs for these)" : "(leave blank for all locations)"}
                  </span>
                </label>
                {locations.length === 0 ? (
                  <p className="text-sm text-zinc-400">No locations yet. Add some under Locations.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-zinc-200 p-2">
                    {locations.map((l) => (
                      <label
                        key={l.id}
                        className="flex items-center gap-2 text-sm cursor-pointer px-2 py-1 hover:bg-zinc-50"
                        data-testid={`user-location-${l.name}`}
                      >
                        <input
                          type="checkbox"
                          checked={form.locations.includes(l.name)}
                          onChange={() => toggleLocation(l.name)}
                          className="accent-brand"
                        />
                        <span className="text-zinc-800">{l.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <button
                type="submit"
                disabled={saving}
                data-testid="user-submit-button"
                className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2 disabled:opacity-60"
              >
                {saving ? "Saving…" : editing ? "Save Changes" : "Create User"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="confirm-delete-user"
              className="bg-red-600 hover:bg-red-700 rounded-none"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkUpload
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        endpoint="/users/bulk"
        templateName="users_template.csv"
        templateContent={USER_CSV_TEMPLATE}
        onDone={load}
        testidPrefix="users-bulk"
      />
    </div>
  );
}
